/**
 * Events List Page
 * 
 * Displays events in list/grid view with:
 * - List/grid view toggle
 * - Future/Past event tabs
 * - Event cards with key information
 * - Create/edit/delete buttons for admin/coach
 * 
 * Requirements: 2.1, 2.2, 2.3, 2.5
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useEventsStore from '../store/eventsStore';
import { useAuth } from '../auth/AuthProvider';
import { Event, restClient, Rider, EventType } from '../api/rest-client';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { LocationLink } from '../components/common/LocationLink';
import { format } from 'date-fns';
import { parseExpectedAttendance, calculateRSVPCounts } from '../utils/rsvpUtils';
import {
  isEventCanceled,
  describeNotificationOutcome,
  describeNotificationFailure,
} from '../utils/eventUpdate';

/**
 * Format date for display
 */
const formatEventDate = (starttime: string, endtime: string): string => {
  const start = new Date(starttime);
  const end = new Date(endtime);
  
  const dateStr = format(start, 'MMM d, yyyy');
  const startTime = format(start, 'h:mm a');
  const endTime = format(end, 'h:mm a');
  
  return `${dateStr} • ${startTime} - ${endTime}`;
};

/**
 * Calculate actual attendance from check-in data (legacy format)
 */
const calculateActualAttendance = (event: Event, riders: Rider[]): { students: number; coaches: number; total: number } => {
  let checkinData: any = {};
  try {
    checkinData = typeof event.event_checkin === 'string' 
      ? JSON.parse(event.event_checkin) 
      : event.event_checkin || {};
  } catch {
    checkinData = {};
  }
  
  const checkedInRiderIds = Object.keys(checkinData).filter(id => checkinData[id] === 1);
  
  const attendedRiders = riders.filter(r => checkedInRiderIds.includes(r.rider_id));
  const coaches = attendedRiders.filter(r => (r.type || 'Student').toLowerCase().includes('coach'));
  const students = attendedRiders.filter(r => !(r.type || 'Student').toLowerCase().includes('coach'));
  
  return {
    students: students.length,
    coaches: coaches.length,
    total: checkedInRiderIds.length
  };
};

/**
 * Event Card Component
 */
interface EventCardProps {
  event: Event;
  onView: (id: string) => void;
  onEdit?: (id: string) => void;
  onCancel?: (id: string) => void;
  isAdmin: boolean;
  riders: Rider[];
  getEventTypeColor: (eventTypeName: string) => string;
}

function EventCard({ event, onView, onEdit, onCancel, isAdmin, riders, getEventTypeColor }: EventCardProps) {
  const isCanceled = isEventCanceled(event); // null/undefined treated as active
  const eventColor = getEventTypeColor(event.event_type);
  
  // Check if event is in the past (already started)
  const now = new Date();
  const eventStart = new Date(event.event_starttime);
  const isPastEvent = eventStart < now;
  
  // Calculate RSVP counts for future events
  const attendance = parseExpectedAttendance(event.event_expectedAttendance);
  const rsvpCounts = calculateRSVPCounts(attendance);
  const hasRSVP = Object.keys(attendance).length > 0;
  
  // Calculate actual attendance for past events
  const actualAttendance = calculateActualAttendance(event, riders);
  const hasAttendance = actualAttendance.total > 0;
  
  return (
    <Card
      className={`${isCanceled ? 'opacity-60' : ''} hover:shadow-lg transition-shadow`}
    >
      <div className="flex flex-col h-full">
        {/* Event Type Badge */}
        <div className="mb-3">
          <span 
            className="inline-block px-3 py-1 text-base font-semibold text-white rounded-full"
            style={{ backgroundColor: eventColor }}
          >
            {event.event_type}
          </span>
          {isCanceled && (
            <span className="ml-2 inline-block px-3 py-1 text-base font-semibold text-white bg-red-500 rounded-full">
              Canceled
            </span>
          )}
        </div>

        {/* Event Name */}
        <h3
          className={`text-lg font-bold text-gray-900 mb-2 ${
            isCanceled ? 'line-through' : ''
          }`}
        >
          {event.event_name}
        </h3>

        {/* Event Details */}
        <div className="flex-grow space-y-2 text-lg text-gray-600 mb-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 mr-2 text-gray-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>{formatEventDate(event.event_starttime, event.event_endtime)}</span>
          </div>

          <div className="flex items-start">
            <svg
              className="w-5 h-5 mr-2 text-gray-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <LocationLink 
              location={event.event_location}
              locationName={event.event_location_name}
              address={event.event_address}
            />
          </div>
        </div>

        {/* RSVP Summary (Future Events) or Actual Attendance (Past Events) */}
        {isPastEvent && hasAttendance ? (
          <div className="mb-4 pb-4 border-t border-gray-200 pt-4">
            <div className="text-xs font-semibold text-gray-600 mb-2 text-center">Attendance</div>
            <div className="grid grid-cols-3 gap-2">
              <div className="flex flex-col items-center p-2 bg-blue-50 rounded-md">
                <span className="text-xs font-medium text-gray-700 mb-1">Students</span>
                <span className="text-lg font-bold text-blue-600">{actualAttendance.students}</span>
              </div>
              <div className="flex flex-col items-center p-2 bg-purple-50 rounded-md">
                <span className="text-xs font-medium text-gray-700 mb-1">Coaches</span>
                <span className="text-lg font-bold text-purple-600">{actualAttendance.coaches}</span>
              </div>
              <div className="flex flex-col items-center p-2 bg-green-50 rounded-md">
                <span className="text-xs font-medium text-gray-700 mb-1">Total</span>
                <span className="text-lg font-bold text-green-600">{actualAttendance.total}</span>
              </div>
            </div>
          </div>
        ) : !isPastEvent && hasRSVP ? (
          <div className="mb-4 pb-4 border-t border-gray-200 pt-4">
            <div className="text-xs font-semibold text-gray-600 mb-2 text-center">RSVP Status</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="flex flex-col items-center p-2 bg-green-50 rounded-md">
                <span className="text-xs font-medium text-gray-700 mb-1">Coming</span>
                <span className="text-lg font-bold text-green-600">{rsvpCounts.coming}</span>
              </div>
              <div className="flex flex-col items-center p-2 bg-yellow-50 rounded-md">
                <span className="text-xs font-medium text-gray-700 mb-1">Maybe</span>
                <span className="text-lg font-bold text-yellow-600">{rsvpCounts.maybe}</span>
              </div>
              <div className="flex flex-col items-center p-2 bg-red-50 rounded-md">
                <span className="text-xs font-medium text-gray-700 mb-1">Not Coming</span>
                <span className="text-lg font-bold text-red-600">{rsvpCounts.notComing}</span>
              </div>
              <div className="flex flex-col items-center p-2 bg-gray-50 rounded-md">
                <span className="text-xs font-medium text-gray-700 mb-1">No Response</span>
                <span className="text-lg font-bold text-gray-900">{rsvpCounts.noResponse}</span>
              </div>
            </div>
          </div>
        ) : null}

        {/* Actions */}
        <div className="flex gap-2 mt-auto">
          <Button
            variant="primary"
            onClick={() => onView(event.event_id)}
            className="flex-1 text-sm py-2"
          >
            View Details
          </Button>
          
          {isAdmin && !isPastEvent && (
            <>
              <Button
                variant="secondary"
                onClick={() => onEdit?.(event.event_id)}
                className="text-sm py-2"
              >
                Edit
              </Button>
              {!isCanceled && (
                <Button
                  variant="danger"
                  onClick={() => onCancel?.(event.event_id)}
                  className="text-sm py-2"
                >
                  Cancel
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * Events List Page Component
 */
export function Events() {
  const navigate = useNavigate();
  
  // Get events from store
  const {
    events,
    loading,
    error,
    fetchEvents,
  } = useEventsStore();

  // Get auth state for role-based actions
  const { isAdmin, isCoach, user } = useAuth();

  // Local state
  const [timeFilter, setTimeFilter] = useState<'future' | 'past'>('future');
  const [season, setSeason] = useState<string>('');
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([]);
  const [riders, setRiders] = useState<Rider[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);

  // Fetch season configuration and event types on mount
  useEffect(() => {
    const fetchSeasonConfig = async () => {
      try {
        const config = await restClient.getSeasonConfig();
        setAvailableSeasons(config.availableSeasons);
        setSeason(config.currentSeason);
      } catch (err) {
        const currentYear = new Date().getFullYear().toString();
        setSeason(currentYear);
        setAvailableSeasons([currentYear]);
      }
    };

    const fetchEventTypes = async () => {
      try {
        const types = await restClient.getEventTypes();
        setEventTypes(types);
      } catch (error) {
        // Silent error handling - will use default colors
      }
    };

    fetchSeasonConfig();
    fetchEventTypes();
  }, []);

  // Compute user's associated rider IDs for non-admin/non-coach filtering
  const userRiderIds = useMemo(() => {
    if (isAdmin || isCoach) return undefined; // Admin/coach see all events
    if (!user) return undefined;
    const associations = user.rider_associations;
    if (!associations || associations.length === 0) return undefined;
    return associations.map(a => a.rider_id);
  }, [isAdmin, isCoach, user]);

  // Fetch events when season changes
  useEffect(() => {
    if (season) {
      fetchEvents(season, userRiderIds);
    }
  }, [season, fetchEvents, userRiderIds]);

  // Fetch active riders count for RSVP calculations
  useEffect(() => {
    if (!season) return;
    
    const fetchActiveRidersCount = async () => {
      try {
        const ridersData = await restClient.getRiders(season);
        setRiders(ridersData);
      } catch (err) {
        // Silent error handling
      }
    };

    fetchActiveRidersCount();
  }, [season]);

  // Filter events by time (future vs past)
  const timeFilteredEvents = useMemo(() => {
    const now = new Date();
    
    return events.filter(event => {
      const eventStart = new Date(event.event_starttime);
      
      if (timeFilter === 'future') {
        return eventStart >= now;
      } else {
        return eventStart < now;
      }
    });
  }, [events, timeFilter]);

  // Sort events by start time
  const sortedEvents = useMemo(() => {
    return [...timeFilteredEvents].sort((a, b) => {
      const timeA = new Date(a.event_starttime).getTime();
      const timeB = new Date(b.event_starttime).getTime();
      return timeFilter === 'future' ? timeA - timeB : timeB - timeA;
    });
  }, [timeFilteredEvents, timeFilter]);

  /**
   * Get color for event type badge
   */
  const getEventTypeColor = useCallback((eventTypeName: string): string => {
    const eventType = eventTypes.find(et => et.name === eventTypeName);
    return eventType?.color || '#435d7d'; // Default to primary blue
  }, [eventTypes]);

  /**
   * Handle view event
   * Requirements: 2.4
   */
  const handleViewEvent = useCallback((id: string) => {
    navigate(`/events/${id}`);
  }, [navigate]);

  /**
   * Handle edit event
   * Requirements: 2.5
   */
  const handleEditEvent = useCallback((id: string) => {
    navigate(`/admin/events/${id}/edit`);
  }, [navigate]);

  /**
   * Handle cancel event
   * Requirements: 2.5
   */
  const handleCancelEvent = useCallback(async (id: string) => {
    const event = events.find(e => e.event_id === id);
    if (!event) return;

    const reason = prompt(
      `Cancel "${event.event_name}"?\n\nThis will send email and GroupMe notifications to all users.\n\nOptional: Enter a reason for cancellation:`,
      ''
    );
    
    if (reason === null) return;

    try {
      await useEventsStore.getState().updateEvent(id, {
        event_id: id,
        event_status: 'canceled',
      });

      // Notify in the background, but surface the outcome instead of discarding
      // it. A swallowed failure is how a 100%-failing endpoint went unnoticed
      // for months. (FR-9)
      const cancelledName = event.event_name;
      restClient.sendCancelNotification(id, reason.trim() || undefined)
        .then((result) => {
          const outcome = describeNotificationOutcome(result);
          if (outcome.message) {
            alert(outcome.message);
          }
        })
        .catch(() => {
          alert(describeNotificationFailure(cancelledName));
        });

      alert(`Event "${event.event_name}" has been canceled successfully.\n\nEmail and GroupMe notifications are being sent in the background to all users.`);

      // Refresh the list. This page previously never refetched after a
      // cancellation, which is why the stale event lingered in view state here
      // specifically. (FR-8.2, BR-36)
      await fetchEvents(season, userRiderIds);
    } catch (error) {
      alert('Failed to cancel event. Please try again.');
    }
  }, [events, fetchEvents, season, userRiderIds]);

  /**
   * Handle create event
   * Requirements: 2.5
   */
  const handleCreateEvent = useCallback(() => {
    navigate('/admin/events/create');
  }, [navigate]);

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-primary-900">Events</h1>
            
            {isAdmin && (
              <Button
                variant="primary"
                onClick={handleCreateEvent}
              >
                Create Event
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-2 md:px-4 pt-3 pb-8">
        {/* Filters */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Time Filter Tabs */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setTimeFilter('future')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                timeFilter === 'future'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Future Events
            </button>
            <button
              onClick={() => setTimeFilter('past')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                timeFilter === 'past'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Past Events
            </button>
          </div>

          {/* Season Filter - Admin Only */}
          {isAdmin && (
            <div className="flex items-center gap-2">
              <label 
                htmlFor="season-select" 
                className="text-sm font-medium text-gray-700"
              >
                Season:
              </label>
              <select
                id="season-select"
                value={season}
                onChange={(e) => setSeason(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-sm"
              >
                {availableSeasons.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {loading && (
            <LoadingSpinner size="sm" />
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Events List */}
      {sortedEvents.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No events found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No {timeFilter} events found
            </p>
            {isAdmin && (
              <div className="mt-6">
                <Button variant="primary" onClick={handleCreateEvent}>
                  Create Event
                </Button>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedEvents.map(event => (
            <EventCard
              key={event.event_id}
              event={event}
              onView={handleViewEvent}
              onEdit={handleEditEvent}
              onCancel={handleCancelEvent}
              isAdmin={isAdmin}
              riders={riders}
              getEventTypeColor={getEventTypeColor}
            />
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

export default Events;
